import React, { useState, useEffect, useRef } from "react";
import { Fade } from "react-awesome-reveal";
import { CSSTransition } from "react-transition-group";
import { workExperience } from "../../config/WorkExperience";
import "./Work.css";

const KEY_CODES = {
  ARROW_UP: "ArrowUp",
  ARROW_DOWN: "ArrowDown",
};

const Work = () => {
  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    // If we're at the end, go to the start
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }
    // If we're at the start, move to the end
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  useEffect(() => {
    if (tabFocus !== null) {
      focusTab();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFocus]);

  const onKeyDown = (e) => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }
      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <section id="experience" className="work-section">
      <h2 className="head">
        <Fade direction="left" duration={300} cascade>
          <div className="span">
            <span>02.</span>
          </div>
          <div className="head-name">Experience</div>
        </Fade>
        <Fade direction="right" cascade>
          <div className="hline"></div>
        </Fade>
      </h2>

      <Fade direction="up" duration={500} triggerOnce>
        <div className="work-inner">
          <div
            className="work-tab-list"
            role="tablist"
            aria-label="Job tabs"
            onKeyDown={onKeyDown}
          >
            {workExperience.map((job, i) => (
              <button
                key={i}
                className={`work-tab-button ${activeTabId === i ? "active" : ""}`}
                onClick={() => setActiveTabId(i)}
                ref={(el) => (tabs.current[i] = el)}
                id={`tab-${i}`}
                role="tab"
                tabIndex={activeTabId === i ? "0" : "-1"}
                aria-selected={activeTabId === i}
                aria-controls={`panel-${i}`}
              >
                <span>{job.company}</span>
              </button>
            ))}
            <div
              className="work-highlight"
              style={{
                transform: `translateY(calc(${activeTabId} * var(--tab-height)))`,
              }}
            />
          </div>

          <div className="work-tab-panels">
            {workExperience.map((job, i) => (
              <CSSTransition
                key={i}
                in={activeTabId === i}
                timeout={250}
                classNames="fade"
              >
                <div
                  className="work-tab-panel"
                  id={`panel-${i}`}
                  role="tabpanel"
                  tabIndex={activeTabId === i ? "0" : "-1"}
                  aria-labelledby={`tab-${i}`}
                  aria-hidden={activeTabId !== i}
                  hidden={activeTabId !== i}
                >
                  <h3 className="work-title">
                    <span>{job.title}</span>
                    <span className="work-company">
                      &nbsp;@&nbsp;
                      <a
                        href={job.url}
                        className="inline-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {job.company}
                      </a>
                    </span>
                  </h3>

                  <p className="work-range">{job.range}</p>

                  <ul className="work-duties">
                    {job.duties.map((duty, j) => (
                      <li key={j}>{duty}</li>
                    ))}
                  </ul>

                  {job.technologies && (
                    <div className="work-technologies">
                      {job.technologies.map((tech, k) => (
                        <span key={k} className="work-tech-tag">{tech}</span>
                      ))}
                    </div>
                  )}
                </div>
              </CSSTransition>
            ))}
          </div>
        </div>
      </Fade>
    </section>
  );
};

export default Work;
